<template>
  <BaseDialog
    :title="displayData.title"
    :btn1-loading="loading"
    :btn1="$t('common.button.save.text')"
    @close="$emit('close')"
    @confirm="handleSave"
    @cancel="$emit('close')"
  >
    <ElInputWrapper>
      <BaseElForm ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <FlexLayout col :gap="20">
          <GrayBlockContainer>
            <!-- 預約服務開始前回覆 -->
            <BaseElFormItem prop="enableReply">
              <template #label>
                <FormItemTooltipLabel :label="displayData.enableReply.label">
                  {{ displayData.enableReply.tooltip }}
                </FormItemTooltipLabel>
              </template>
              <BaseElSwitch
                v-model="formData.enableReply"
                :active-text="displayData.switch.open"
                :inactive-text="displayData.switch.close"
              />
            </BaseElFormItem>

            <!-- 開放回覆區間 -->
            <BaseElFormItem :label="displayData.replyPeriod.label" :required="formData.enableReply">
              <FlexLayout col :gap="16">
                <!-- 開放回覆時間 -->
                <FlexLayout :gap="8" align="center" class="text-normal text-gray-100">
                  {{ displayData.replyStart.label }}
                  <BaseElFormItem prop="replyStartGroup">
                    <FlexLayout :gap="8" align="center">
                      <BaseElFormItem prop="replyStartConfig.offsetUnit" :show-message="false">
                        <BaseElInput
                          v-model.number="formData.replyStartConfig.offsetUnit"
                          type="number"
                          :step="1"
                          :min="0"
                          :disabled="!formData.enableReply"
                          class="!w-[78px]"
                        />
                      </BaseElFormItem>
                      <BaseElFormItem prop="replyStartConfig.offsetUnitType" :show-message="false">
                        <TimeUnitSelect
                          :value.sync="formData.replyStartConfig.offsetUnitType"
                          :disabled="!formData.enableReply"
                          class="!w-[78px]"
                          :allows="['minute', 'hour', 'day']"
                        />
                      </BaseElFormItem>
                    </FlexLayout>
                  </BaseElFormItem>
                  {{ displayData.replyStart.suffix }}
                  <TipInfo :size="16">{{ displayData.replyStart.tooltip }}</TipInfo>
                </FlexLayout>

                <!-- 回覆截止時間 -->
                <FlexLayout :gap="8" align="center" class="text-normal text-gray-100">
                  {{ displayData.replyEnd.label }}
                  <BaseElFormItem prop="replyEndGroup">
                    <FlexLayout :gap="8" align="center">
                      <BaseElFormItem prop="replyEndConfig.offsetUnit" :show-message="false">
                        <BaseElInput
                          v-model.number="formData.replyEndConfig.offsetUnit"
                          type="number"
                          :step="1"
                          :min="0"
                          :disabled="!formData.enableReply"
                          class="!w-[78px]"
                        />
                      </BaseElFormItem>
                      <BaseElFormItem prop="replyEndConfig.offsetUnitType" :show-message="false">
                        <TimeUnitSelect
                          :value.sync="formData.replyEndConfig.offsetUnitType"
                          :disabled="!formData.enableReply"
                          class="!w-[78px]"
                          :allows="['minute', 'hour', 'day']"
                        />
                      </BaseElFormItem>
                    </FlexLayout>
                  </BaseElFormItem>
                  {{ displayData.replyEnd.suffix }}
                  <TipInfo :size="16">{{ displayData.replyEnd.tooltip }}</TipInfo>
                </FlexLayout>
              </FlexLayout>
            </BaseElFormItem>

            <BaseElFormItem prop="enableNoReplyAutoCancel">
              <template #label>
                <FormItemTooltipLabel :label="displayData.enableNoReplyAutoCancel.label">
                  {{ displayData.enableNoReplyAutoCancel.tooltip }}
                </FormItemTooltipLabel>
              </template>
              <BaseElSwitch
                v-model="formData.enableNoReplyAutoCancel"
                :active-text="displayData.switch.open"
                :inactive-text="displayData.switch.close"
                :disabled="!formData.enableReply"
              />
            </BaseElFormItem>

            <!-- 自動取消時間 -->
            <BaseElFormItem :label="displayData.autoCancelTime.label" :required="formData.enableNoReplyAutoCancel">
              <FlexLayout col :gap="16">
                <FlexLayout :gap="8" align="center" class="text-normal text-gray-100">
                  {{ displayData.autoCancelConfig.label }}
                  <BaseElFormItem prop="noReplyAutoCancelGroup">
                    <FlexLayout :gap="8" align="center">
                      <BaseElFormItem prop="noReplyAutoCancelConfig.offsetUnit" :show-message="false">
                        <BaseElInput
                          v-model.number="formData.noReplyAutoCancelConfig.offsetUnit"
                          type="number"
                          :step="1"
                          :min="0"
                          :disabled="!formData.enableNoReplyAutoCancel"
                          class="!w-[78px]"
                        />
                      </BaseElFormItem>
                      <BaseElFormItem prop="noReplyAutoCancelConfig.offsetUnitType" :show-message="false">
                        <TimeUnitSelect
                          :value.sync="formData.noReplyAutoCancelConfig.offsetUnitType"
                          :disabled="!formData.enableNoReplyAutoCancel"
                          class="!w-[78px]"
                          :allows="['minute', 'hour', 'day']"
                        />
                      </BaseElFormItem>
                    </FlexLayout>
                  </BaseElFormItem>
                  {{ displayData.autoCancelConfig.suffix }}
                </FlexLayout>
              </FlexLayout>
            </BaseElFormItem>

            <!-- 未回覆自動取消提示 -->
            <BaseElFormItem :label="displayData.autoCancelText.label" prop="noReplyAutoCancelText">
              <BaseElInput
                v-model="formData.noReplyAutoCancelText"
                type="textarea"
                :rows="3"
                :maxlength="25"
                show-word-limit
                :disabled="!formData.enableNoReplyAutoCancel"
                :placeholder="displayData.autoCancelText.placeholder"
              />
            </BaseElFormItem>
          </GrayBlockContainer>

          <!-- 預覽區域 -->
          <BaseElFormItem v-show="formData.enableReply" :label="displayData.preview.title" class="mb-0">
            <FlexLayout col :gap="8" class="text-sub leading-[20px]">
              <FlexLayout :gap="8">
                <p class="w-[160px]">{{ displayData.preview.reservationTime.label }}</p>
                <p class="text-gray-100">{{ displayHint.summary.reservationTime }}</p>
              </FlexLayout>
              <FlexLayout :gap="8">
                <p class="w-[160px]">{{ displayData.replyPeriod.label }}</p>
                <p :class="displayHint.summary.replyPeriod === '-' || 'text-primary-100'">
                  {{ displayHint.summary.replyPeriod }}
                </p>
              </FlexLayout>
              <FlexLayout v-if="formData.enableNoReplyAutoCancel" :gap="8">
                <p class="w-[160px]">{{ displayData.autoCancelTime.label }}</p>
                <p :class="displayHint.summary.autoCancel === '-' || 'text-primary-100'">
                  {{ displayHint.summary.autoCancel }}
                </p>
              </FlexLayout>
            </FlexLayout>
          </BaseElFormItem>

          <!-- 提示訊息，顯示 displayhint的hint1以及hint2 文字內容 -->
          <GrayBlockContainer v-for="(item, index) in [displayHint.hint1, displayHint.hint2]" v-show="formData.enableReply" :key="index" class="!p-[10px] font-normal">
            <div class="flex items-center gap-[8px]">
              <MaterialIcon color="var(--primary-100)" size="24">info_outline</MaterialIcon>
              {{ item }}
            </div>
          </GrayBlockContainer>
        </FlexLayout>
      </BaseElForm>
    </ElInputWrapper>
    <!-- 儲存提示彈窗 -->
    <BaseDialog
      v-if="showNoticeDialog"
      :title="displayData.notice.title"
      :btn1-loading="loading"
      @confirm="confirmSave"
      @close="showNoticeDialog = false"
      @cancel="showNoticeDialog = false"
    >
      <FlexLayout col :gap="16">
        <p class="text-normal text-gray-100">
          {{ displayData.notice.content }}
        </p>
        <ul class="text-normal text-gray-100 list-decimal list-inside">
          <li v-html="displayData.notice.rule1" />
          <li v-html="displayData.notice.rule2" />
        </ul>
      </FlexLayout>
    </BaseDialog>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, ref, watch } from 'vue'
import { get, isNull, isUndefined, set } from 'lodash'
import dayjs from 'dayjs'
import { i18n } from '@/plugins/i18n/i18n'
import TimeUnitSelect from '@/components/Select/TimeUnitSelect.vue'
import FlexLayout from '@/components/FlexLayout.vue'
import { UpdateReservationParameter } from '@/api/reservation'
import { useShop } from '@/use/shop'
import { onFormRulesChangeClearValidate, useBaseForm } from '@/use/useForm'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import ElInputWrapper from '@/components/ElInputWrapper.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { noEmptyRules, maxRules } from '@/validation'
import TipInfo from '@/components/TipInfo.vue'
import formUtils from '@/utils/form'

export default defineComponent({
  name: 'EditReservationReplySettingsModal',
  components: {
    BaseDialog,
    TimeUnitSelect,
    FlexLayout,
    GrayBlockContainer,
    FormItemTooltipLabel,
    ElInputWrapper,
    TipInfo,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formData, formRef, initFormData, checkForm, loading } = useBaseForm()
    const showNoticeDialog = ref(false)

    // 初始化表單資料
    initFormData({
      enableReply: get(props.configData, 'enableReply', false),
      replyStartConfig: {
        offsetUnit: get(props.configData, 'replyStartConfig.offsetUnit', null),
        offsetUnitType: get(props.configData, 'replyStartConfig.offsetUnitType', 'day'),
      },
      replyEndConfig: {
        offsetUnit: get(props.configData, 'replyEndConfig.offsetUnit', null),
        offsetUnitType: get(props.configData, 'replyEndConfig.offsetUnitType', 'day'),
      },
      enableNoReplyAutoCancel: get(props.configData, 'enableNoReplyAutoCancel', false),
      noReplyAutoCancelConfig: {
        offsetUnit: get(props.configData, 'noReplyAutoCancelConfig.offsetUnit', null),
        offsetUnitType: get(props.configData, 'noReplyAutoCancelConfig.offsetUnitType', 'day'),
      },
      noReplyAutoCancelText: get(props.configData, 'noReplyAutoCancelText') || '逾期未回覆系統將自動取消預約',
    })

    // 時間單位轉換為分鐘
    const convertToMinutes = (value, unit) => {
      if (isNull(value) || isUndefined(value)) return 0
      const unitMap = { minute: 1, hour: 60, day: 1440 }
      return value * (unitMap[unit] || 1)
    }

    // 自訂驗證：開放回覆時間必填
    const validateReplyStartTime = (rule, value, callback) => {
      if (!formData.enableReply) {
        callback()
        return
      }

      const startValue = formData.replyStartConfig.offsetUnit

      // 檢查是否為空
      if (isNull(startValue) || isUndefined(startValue) || startValue === '') {
        callback(new Error(i18n.t('validation.noEmptyRules.default.text')))
        return
      }

      callback()
    }

    // 自訂驗證：回覆截止時間必須早於開放回覆時間
    const validateReplyEndTime = (rule, value, callback) => {
      if (!formData.enableReply) {
        callback()
        return
      }

      const startValue = formData.replyStartConfig.offsetUnit
      const endValue = formData.replyEndConfig.offsetUnit

      // 檢查是否為空
      if (isNull(endValue) || isUndefined(endValue) || endValue === '') {
        callback(new Error(i18n.t('validation.noEmptyRules.default.text')))
        return
      }

      const startMinutes = convertToMinutes(startValue, formData.replyStartConfig.offsetUnitType)
      const endMinutes = convertToMinutes(endValue, formData.replyEndConfig.offsetUnitType)

      if (endMinutes >= startMinutes) {
        callback(new Error(i18n.t('reservationReplySettings.dialog.validation.replyEndMustBeforeStart')))
        return
      }

      callback()
    }

    // 自訂驗證：自動取消時間必須早於回覆截止時間
    const validateAutoCancelTime = (rule, value, callback) => {
      if (!formData.enableNoReplyAutoCancel) {
        callback()
        return
      }

      const cancelValue = formData.noReplyAutoCancelConfig.offsetUnit

      // 檢查是否為空
      if (isNull(cancelValue) || isUndefined(cancelValue) || cancelValue === '') {
        callback(new Error(i18n.t('validation.noEmptyRules.default.text')))
        return
      }

      // 如果啟用回覆，自動取消時間必須早於回覆截止時間
      if (formData.enableReply) {
        const endValue = formData.replyEndConfig.offsetUnit
        const endMinutes = convertToMinutes(endValue, formData.replyEndConfig.offsetUnitType)
        const cancelMinutes = convertToMinutes(cancelValue, formData.noReplyAutoCancelConfig.offsetUnitType)

        if (cancelMinutes >= endMinutes) {
          callback(new Error(i18n.t('reservationReplySettings.dialog.validation.autoCancelMustBeforeEnd')))
          return
        }
      }

      callback()
    }

    // 表單驗證規則 - 動態根據開關狀態決定是否驗證
    const formRules = computed(() => {
      const rules = {}

      // 只在啟用回覆時才驗證回覆相關欄位
      if (formData.enableReply) {
        rules.replyStartGroup = [
          { validator: validateReplyStartTime, trigger: ['blur', 'change'] },
        ]
        rules['replyStartConfig.offsetUnit'] = [
          { validator: () => formUtils.validateField(formRef.value, 'replyStartGroup'), trigger: ['blur', 'change'] },
        ]
        rules['replyStartConfig.offsetUnitType'] = [
          { validator: () => formUtils.validateField(formRef.value, 'replyStartGroup'), trigger: ['blur', 'change'] },
        ]
        rules.replyEndGroup = [
          { validator: validateReplyEndTime, trigger: ['blur', 'change'] },
        ]
        rules['replyEndConfig.offsetUnit'] = [
          { validator: () => formUtils.validateField(formRef.value, 'replyEndGroup'), trigger: ['blur', 'change'] },
        ]
        rules['replyEndConfig.offsetUnitType'] = [
          { validator: () => formUtils.validateField(formRef.value, 'replyEndGroup'), trigger: ['blur', 'change'] },
        ]
      }

      // 只在啟用自動取消時才驗證取消相關欄位
      if (formData.enableNoReplyAutoCancel) {
        rules.noReplyAutoCancelGroup = [
          { validator: validateAutoCancelTime, trigger: ['blur', 'change'] },
        ]
        rules['noReplyAutoCancelConfig.offsetUnit'] = [
          { validator: () => formUtils.validateField(formRef.value, 'noReplyAutoCancelGroup'), trigger: ['blur', 'change'] },
        ]
        rules['noReplyAutoCancelConfig.offsetUnitType'] = [
          { validator: () => formUtils.validateField(formRef.value, 'noReplyAutoCancelGroup'), trigger: ['blur', 'change'] },
        ]
        rules.noReplyAutoCancelText = [noEmptyRules(), maxRules(25)]
      }

      return rules
    })

    // 顯示資料
    const displayData = computed(() => ({
      title: i18n.t('reservationReplySettings.dialog.title'),
      enableReply: {
        label: i18n.t('reservationReplySettings.enableReply.label'),
        tooltip: i18n.t('reservationReplySettings.enableReply.tooltip'),
      },
      replyPeriod: {
        label: i18n.t('reservationReplySettings.dialog.replyPeriod.label'),
      },
      replyStart: {
        label: i18n.t('reservationReplySettings.dialog.replyStart.label'),
        suffix: i18n.t('reservationReplySettings.dialog.replyStart.suffix'),
        tooltip: i18n.t('reservationReplySettings.replyStartTime.tooltip'),
      },
      replyEnd: {
        label: i18n.t('reservationReplySettings.dialog.replyEnd.label'),
        suffix: i18n.t('reservationReplySettings.dialog.replyEnd.suffix'),
        tooltip: i18n.t('reservationReplySettings.replyEndTime.tooltip'),
      },
      enableNoReplyAutoCancel: {
        label: i18n.t('reservationReplySettings.enableNoReplyAutoCancel.label'),
        tooltip: i18n.t('reservationReplySettings.enableNoReplyAutoCancel.tooltip'),
      },
      autoCancelTime: {
        label: i18n.t('reservationReplySettings.dialog.autoCancelTime.label'),
      },
      autoCancelConfig: {
        label: i18n.t('reservationReplySettings.dialog.autoCancelConfig.label'),
        suffix: i18n.t('reservationReplySettings.dialog.autoCancelConfig.suffix'),
      },
      autoCancelText: {
        label: i18n.t('reservationReplySettings.dialog.autoCancelText.label'),
        placeholder: i18n.t('reservationReplySettings.dialog.autoCancelText.placeholder'),
      },
      preview: {
        title: i18n.t('reservationReplySettings.dialog.preview.title'),
        reservationTime: {
          label: i18n.t('reservationReplySettings.dialog.preview.reservationTime'),
        },
      },
      notice: {
        title: i18n.t('reservationReplySettings.dialog.notice.title'),
        content: i18n.t('reservationReplySettings.dialog.notice.content'),
        rule1: i18n.t('reservationReplySettings.dialog.notice.rule1'),
        rule2: i18n.t('reservationReplySettings.dialog.notice.rule2'),
      },
      switch: {
        open: i18n.t('common.open.text'),
        close: i18n.t('common.close.text'),
      },
    }))

    // 顯示提示文字 - 使用當前時間計算
    const displayHint = computed(() => {
      const now = new Date()
      const nowTime = dayjs(now).format('YYYY/MM/DD HH:mm')

      const hint = {
        summary: {
          reservationTime: nowTime,
          replyPeriod: '-',
          autoCancel: '-',
        },
        hint1: i18n.t('reservationReplySettings.dialog.rule1'),
        hint2: i18n.t('reservationReplySettings.dialog.rule2'),
      }

      // 計算回覆區間提示
      if (formData.enableReply &&
          formData.replyStartConfig.offsetUnit &&
          formData.replyStartConfig.offsetUnitType &&
          formData.replyEndConfig.offsetUnit &&
          formData.replyEndConfig.offsetUnitType) {
        const startValue = formData.replyStartConfig.offsetUnit
        const startUnit = formData.replyStartConfig.offsetUnitType
        const endValue = formData.replyEndConfig.offsetUnit
        const endUnit = formData.replyEndConfig.offsetUnitType

        if (!isNull(startValue) && !isUndefined(startValue) &&
            !isNull(endValue) && !isUndefined(endValue)) {
          const replyStartTime = dayjs(now).subtract(startValue, startUnit)
          const replyEndTime = dayjs(now).subtract(endValue, endUnit)

          hint.summary.replyPeriod = i18n.t('reservationReplySettings.dialog.preview.example.replyPeriod', {
            replyStart: replyStartTime.format('YYYY/MM/DD HH:mm'),
            replyEnd: replyEndTime.format('YYYY/MM/DD HH:mm'),
          })
        }
      }

      // 計算自動取消提示
      if (formData.enableNoReplyAutoCancel &&
          formData.noReplyAutoCancelConfig.offsetUnit &&
          formData.noReplyAutoCancelConfig.offsetUnitType) {
        const cancelValue = formData.noReplyAutoCancelConfig.offsetUnit
        const cancelUnit = formData.noReplyAutoCancelConfig.offsetUnitType

        if (!isNull(cancelValue) && !isUndefined(cancelValue)) {
          const autoCancelTime = dayjs(now).subtract(cancelValue, cancelUnit)

          hint.summary.autoCancel = i18n.t('reservationReplySettings.dialog.preview.example.autoCancel', {
            autoCancelTime: autoCancelTime.format('YYYY/MM/DD HH:mm'),
          })
        }
      }

      return hint
    })

    // 提交資料
    const submitData = computed(() => {
      const data = {
        enableReply: formData.enableReply,
        enableNoReplyAutoCancel: formData.enableNoReplyAutoCancel,
      }
      if (formData.enableReply) {
        set(data, 'replyStartConfig', formData.replyStartConfig)
        set(data, 'replyEndConfig', formData.replyEndConfig)
      }
      if (formData.enableNoReplyAutoCancel) {
        set(data, 'noReplyAutoCancelConfig', formData.noReplyAutoCancelConfig)
        set(data, 'noReplyAutoCancelText', formData.noReplyAutoCancelText)
      }
      return data
    })

    // 儲存處理
    const handleSave = async () => {
      if (loading.value) return

      const valid = await checkForm(formRef.value)
      if (!valid) return

      // 如果啟用自動取消，顯示提示彈窗
      if (formData.enableNoReplyAutoCancel) {
        showNoticeDialog.value = true
      } else {
        await confirmSave()
      }
    }

    // 確認儲存
    const confirmSave = async () => {
      if (loading.value) return
      loading.value = true

      try {
        const payload = {
          shopId: shopId.value,
          ...submitData.value,
        }

        const [, err] = await UpdateReservationParameter(payload)
        if (err) throw err

        // 成功
        showNoticeDialog.value = false
        emit('refresh')
        emit('close')
        window.$message.success(i18n.t('common.message.updateSuccess'))
      } catch (error) {
        // 顯示錯誤訊息
        window.$message.error(i18n.t('common.message.updateFail', { message: error || i18n.t('common.message.unknownError') }))
      } finally {
        loading.value = false
      }
    }

    // 監聽 enableReply 變更，關閉自動取消功能
    watch(() => formData.enableReply, (newVal) => {
      if (!newVal) {
        formData.enableNoReplyAutoCancel = false
      }
    })

    onFormRulesChangeClearValidate(formRef, formRules)

    return {
      formRef,
      formData,
      formRules,
      displayData,
      displayHint,
      loading,
      showNoticeDialog,
      handleSave,
      confirmSave,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
